import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/api";
import { payWithRazorpay } from "@/razorpay";
import { Reveal, Overline, Magnetic } from "@/components/Motion";
import { RedDot } from "@/components/Logo";

function List({ title, items, dot = true }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-4">{title}</h3>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-ink/70 leading-relaxed">
            {dot && <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0 mt-1.5" />} {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RegisterForm({ workshop }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", age: "", profession: "", city: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [regInfo, setRegInfo] = useState(null);
  const [payUrl, setPayUrl] = useState(null);

  useEffect(() => {
    api.get("/config").then((r) => setPayUrl(r.data.payment_page_url)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/workshops/${workshop.id}/register`, form);
      const reg = res.data;
      if (reg.razorpay_order) {
        const cfg = (await api.get("/config")).data;
        await payWithRazorpay({
          order: reg.razorpay_order,
          keyId: cfg.razorpay_key_id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          description: workshop.title,
          onSuccess: async (resp) => {
            await api.post(`/registrations/${reg.id}/verify`, resp);
            toast.success("Payment received — your seat is confirmed.");
            setDone(true);
          },
          onDismiss: () => toast.error("Payment incomplete — your registration is saved as pending."),
        });
      } else {
        toast.success(
          workshop.fee > 0
            ? "Registration received — the studio will share payment details by email."
            : "Registration confirmed — see you at the workshop."
        );
        setRegInfo(reg);
        setDone(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="registration-success" className="border border-brand/40 bg-brand/5 p-8 text-center">
        <RedDot className="w-3 h-3 mx-auto" />
        <p className="font-display text-2xl mt-4">You're registered.</p>
        {(workshop.payment_link || payUrl) && regInfo?.fee > 0 ? (
          <>
            <p className="text-sm text-paper/60 mt-2">One step left — complete the fee to confirm your seat.</p>
            <a href={workshop.payment_link || payUrl} target="_blank" rel="noreferrer" data-testid="workshop-pay-btn"
              className="mt-6 inline-block bg-brand text-paper px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors">
              Pay {inr(regInfo.fee)} via Razorpay →
            </a>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/40">
              Mention your name in the payment note
            </p>
          </>
        ) : (
          <p className="text-sm text-paper/60 mt-2">A confirmation will reach your inbox shortly.</p>
        )}
      </div>
    );
  }

  const inputCls = "w-full bg-transparent border-b border-paper/25 focus:border-brand outline-none py-3 text-base text-paper placeholder:text-paper/30 transition-colors";

  return (
    <form onSubmit={submit} data-testid="workshop-registration-form" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <input required data-testid="reg-name" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
        <input required type="email" data-testid="reg-email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
        <input required data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={inputCls} />
        <input data-testid="reg-age" value={form.age} onChange={set("age")} placeholder="Age" className={inputCls} />
        <input data-testid="reg-profession" value={form.profession} onChange={set("profession")} placeholder="Profession / College" className={inputCls} />
        <input data-testid="reg-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
      </div>
      <textarea rows={3} data-testid="reg-notes" value={form.notes} onChange={set("notes")}
        placeholder="Anything we should know? (diet, access, experience…)" className={`${inputCls} resize-none`} />
      <Magnetic>
        <button type="submit" disabled={busy} data-testid="register-submit-btn"
          className="bg-brand text-paper px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-brand-hover transition-colors disabled:opacity-50">
          {busy ? "Processing…" : workshop.fee > 0 ? `Register — ${inr(workshop.fee)}` : "Register — Free"}
        </button>
      </Magnetic>
    </form>
  );
}

export default function WorkshopDetail() {
  const { id } = useParams();
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get(`/workshops/${id}`).then((r) => setW(r.data)).catch(() => setW(false));
  }, [id]);

  if (w === false) return <main className="pt-40 px-6 md:px-12 min-h-screen"><p className="font-mono text-sm">Workshop not found.</p></main>;
  if (!w) return <main className="pt-40 px-6 md:px-12 min-h-screen"><p className="font-mono text-sm text-ink/40">Loading…</p></main>;

  const isUpcoming = w.status === "upcoming";

  return (
    <main data-testid="workshop-detail" className="pt-28 pb-24">
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <Link to="/workshops" data-testid="back-to-workshops" className="link-line inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/60">
          <ArrowLeft size={14} /> All Workshops
        </Link>
        <Reveal className="mt-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] flex items-center gap-2 text-brand">
            {isUpcoming && <RedDot className="w-2 h-2 animate-pulse-dot" />}
            {isUpcoming ? "Registration open" : "Past workshop — archive"}
          </p>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl tracking-tight mt-4 leading-[1.05] max-w-4xl">{w.title}</h1>
        </Reveal>
      </div>
      <Reveal className="mt-12 px-6 md:px-12 max-w-[1600px] mx-auto">
        <div className="frame-clip spotlight aspect-[16/8]">
          <img src={w.cover} alt={w.title} className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <Reveal className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-6 border-y border-ink/15 py-8">
          {[["Dates", w.dates], ["Duration", w.duration], ["Location", w.location],
            ["Participants", w.participants], ["Fee", isUpcoming ? inr(w.fee) : inr(w.fee)]].map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40">{k}</p>
              <p className="font-display text-lg mt-2">{v}</p>
            </div>
          ))}
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-16">
          <Reveal className="lg:col-span-7 space-y-12">
            <p className="font-display text-2xl md:text-3xl leading-snug tracking-tight">{w.intro}</p>
            <List title="What you will do" items={w.content} />
            <List title="Schedule" items={w.schedule} />
            {w.eligibility && (
              <div>
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-4">Who it's for</h3>
                <p className="text-sm text-ink/70 leading-relaxed">{w.eligibility}</p>
              </div>
            )}
            {w.accommodation && (
              <div>
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-4">Stay</h3>
                <p className="text-sm text-ink/70 leading-relaxed">{w.accommodation}</p>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 space-y-10">
            <div className="bg-linen border border-ink/10 p-6 space-y-8">
              <List title="Mentors" items={[w.mentors]} dot={false} />
              <List title="Includes" items={w.inclusions} />
              <List title="Does not include" items={w.exclusions} />
            </div>
            {!!(w.faqs || []).length && (
              <div>
                <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-brand mb-4">FAQ</h3>
                {w.faqs.map((f, i) => (
                  <details key={i} className="border-b border-ink/10 py-3 group" data-testid={`faq-${i}`}>
                    <summary className="font-display text-lg cursor-pointer list-none flex justify-between items-center">
                      {f.q}<span className="text-brand group-open:rotate-45 transition-transform inline-block">+</span>
                    </summary>
                    <p className="text-sm text-ink/60 mt-2 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            )}
          </Reveal>
        </div>
        {!!(w.gallery || []).length && (
          <div className="mt-20">
            <Overline>Gallery</Overline>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {w.gallery.map((g, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="frame-clip spotlight aspect-[4/3]">
                    <img src={g} alt={`${w.title} — ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}
        {!!(w.terms || []).length && (
          <Reveal className="mt-16">
            <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-ink/40 mb-4">Terms</h3>
            <ul className="space-y-1 text-xs text-ink/50 font-mono">
              {w.terms.map((t, i) => <li key={i}>· {t}</li>)}
            </ul>
          </Reveal>
        )}
        {isUpcoming && (
          <div id="register" className="mt-24 scroll-mt-32">
            <Reveal className="bg-ink text-paper p-8 md:p-14">
              <Overline dark>Register</Overline>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 mb-10">Reserve your seat.</h2>
              <RegisterForm workshop={w} />
            </Reveal>
          </div>
        )}
      </div>
    </main>
  );
}
